import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrophyIcon, Medal } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import useTranslation from '@/hooks/use-translation';
import type { SectorType } from '@/contexts/SectorContext';
import { cn } from '@/lib/utils';

interface LeaderboardCardProps {
  className?: string;
  sector: SectorType;
}

const LeaderboardCard: React.FC<LeaderboardCardProps> = ({ className, sector }) => {
  const { isRTL } = useLanguage();
  const { translate } = useTranslation();

  const getLeaderboardData = () => {
    if (sector === 'household') {
      return [
        { rank: 1, name: "Sarah Johnson", score: 95, savings: "2,500 gal" },
        { rank: 2, name: "Michael Chen", score: 92, savings: "2,300 gal" },
        { rank: 3, name: "Emma Davis", score: 88, savings: "2,100 gal" },
        { rank: 4, name: "You", score: 86, savings: "2,000 gal" },
        { rank: 5, name: "David Wilson", score: 84, savings: "1,900 gal" }
      ];
    } else {
      return [
        { rank: 1, name: "Green Valley Farm", score: 95, savings: "25,000 gal" },
        { rank: 2, name: "Sunset Agriculture", score: 92, savings: "23,000 gal" },
        { rank: 3, name: "River Creek Fields", score: 88, savings: "21,000 gal" },
        { rank: 4, name: "You", score: 86, savings: "20,000 gal" },
        { rank: 5, name: "Blue Sky Farms", score: 84, savings: "19,000 gal" }
      ];
    }
  };

  const leaderboardData = getLeaderboardData();

  return (
    <Card className={cn("overflow-hidden", className)}>
      <CardHeader className="space-y-0 pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <TrophyIcon className="h-5 w-5 text-yellow-500 flex-shrink-0" />
          <span className="truncate">
            {translate(sector === 'household' ? "Water Conservation Leaders" : "Irrigation Efficiency Leaders")}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {leaderboardData.map((entry) => (
            <div
              key={entry.rank}
              className={cn(
                "flex items-center justify-between p-3 rounded-lg transition-colors",
                entry.name === "You" ? "bg-primary/10" : "hover:bg-muted/80 bg-muted/50"
              )}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-full bg-background flex items-center justify-center flex-shrink-0">
                  {entry.rank <= 3 ? (
                    <Medal className={cn(
                      "h-5 w-5",
                      entry.rank === 1 ? "text-yellow-500" :
                      entry.rank === 2 ? "text-gray-400" :
                      "text-amber-600"
                    )} />
                  ) : (
                    <span className="text-sm font-medium">{entry.rank}</span>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="font-medium truncate">{entry.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {translate("Score")}: {entry.score}
                  </p>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="font-medium">{entry.savings}</p>
                <p className="text-sm text-muted-foreground">{translate("Saved")}</p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default LeaderboardCard;
